// component/device-components/DeviceTypeManager.jsx
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { toast } from "react-toastify";
import deviceTypeStore from "../../stores/DeviceTypeStore";
import DeviceTypeGrid from "./DeviceTypeGrid";
import DeviceTypeList from "./DeviceTypeList";
import DeviceImageUploader from "../DeviceImageUploader";

const DeviceTypeManager = observer(() => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deviceTypeData, setDeviceTypeData] = useState({
    type_name: "",
    manufacture: "",
    image_url: "",
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deviceTypeToDelete, setDeviceTypeToDelete] = useState(null);
  const [viewMode, setViewMode] = useState("grid");

  useEffect(() => {
    deviceTypeStore.fetchDeviceTypes();
  }, []);

  const handleOpenModal = (deviceType = null) => {
    if (deviceType) {
      setDeviceTypeData({ ...deviceType });
      setEditMode(true);
    } else {
      setDeviceTypeData({ type_name: "", manufacture: "", image_url: "" });
      setEditMode(false);
    }
    setIsModalOpen(true);
  };

  const handleOpenDeleteDialog = (deviceType) => {
    setDeviceTypeToDelete(deviceType);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deviceTypeToDelete) {
      await deviceTypeStore.deleteDeviceType(deviceTypeToDelete.id);
      toast.success("Device type deleted successfully!");
    }
    setIsDeleteDialogOpen(false);
  };

  const handleSave = async () => {
    try {
      if (editMode) {
        await deviceTypeStore.updateDeviceType(
          deviceTypeData.id,
          deviceTypeData
        );
        toast.success("Device type updated successfully!");
      } else {
        await deviceTypeStore.createDeviceType(deviceTypeData);
        toast.success("Device type added successfully!");
      }
      setIsModalOpen(false);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(
          "A device type with this name and manufacture already exists."
        );
      } else {
        toast.error("Error saving device type. Please try again.");
      }
    }
  };

  const handleViewChange = (_, newView) => {
    if (newView) setViewMode(newView);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box className="flex justify-between items-center mb-4">
        <Button
          variant="contained"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            backgroundColor: "#22C55E",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#16A34A" },
          }}
          onClick={() => handleOpenModal()}
        >
          Add Device Type
        </Button>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          aria-label="View Mode"
        >
          <ToggleButton value="grid">Grid View</ToggleButton>
          <ToggleButton value="table">Table View</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === "grid" ? (
        <DeviceTypeGrid
          deviceTypes={deviceTypeStore.deviceTypes}
          onEdit={handleOpenModal}
          onDelete={handleOpenDeleteDialog}
          loading={deviceTypeStore.isLoading}
        />
      ) : (
        <DeviceTypeList
          deviceTypes={deviceTypeStore.deviceTypes}
          onEdit={handleOpenModal}
          onDelete={handleOpenDeleteDialog}
          loading={deviceTypeStore.isLoading}
        />
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogTitle>
          {editMode ? "Edit Device Type" : "Add New Device Type"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Type Name"
            fullWidth
            margin="normal"
            value={deviceTypeData.type_name}
            onChange={(e) =>
              setDeviceTypeData((prev) => ({
                ...prev,
                type_name: e.target.value,
              }))
            }
          />
          <TextField
            label="Manufacture"
            fullWidth
            margin="normal"
            value={deviceTypeData.manufacture}
            onChange={(e) =>
              setDeviceTypeData((prev) => ({
                ...prev,
                manufacture: e.target.value,
              }))
            }
          />
          <DeviceImageUploader
            initialValue={deviceTypeData.image_url}
            onSelectImage={(imageUrl) =>
              setDeviceTypeData((prev) => ({ ...prev, image_url: imageUrl }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsModalOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography color="error" gutterBottom>
            Warning: Deleting this device type may affect existing devices!
          </Typography>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{deviceTypeToDelete?.type_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteDialogOpen(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default DeviceTypeManager;
